import { faPen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Group } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { defaultPersonAttributes, FieldCollection } from '@feathr/blackbox';
import { Button } from '@feathr/components';
import CustomFieldSelect from '@feathr/extender/components/CustomFieldSelect';
import { useStore } from '@feathr/extender/state';

import CreateImportFieldModal from './CreateImportFieldModal';
import { matchBuiltInField } from './DataImportStepTwo.utils';
import type { IImportMappingCellProps } from './ImportMappingTable.utils';

/** Which way the create-field modal opened: a fresh field, or the row's existing new field. */
type TModalMode = 'create' | 'edit';

/**
 * The mapping row's Feathr field. Creating a field opens a modal rather than inline inputs, and a
 * new, unsaved field can be edited from the pencil beside it.
 */
function ImportMappingFieldCell({
  column,
  importer,
  disabled,
}: Readonly<IImportMappingCellProps>): JSX.Element {
  const { t } = useTranslation();
  const { CustomFields } = useStore();
  const [modalMode, setModalMode] = useState<TModalMode | undefined>(undefined);

  // A column named for a built-in field (e.g. "email") starts out mapped to it.
  useEffect(() => {
    matchBuiltInField(column);
    importer.setAttributeDirty('column_mappers');
  }, [column, importer]);

  const fieldId = column.feathr_attr;
  /*
   * Any default attribute id, opt-out included, is never a custom field: looking one up in
   * CustomFields would create a placeholder model and request it from the API.
   */
  const isDefaultAttribute = !!fieldId && defaultPersonAttributes.some(({ id }) => id === fieldId);
  const customField = fieldId && !isDefaultAttribute ? CustomFields.get(fieldId) : undefined;
  const newField = customField?.isEphemeral ? customField : undefined;

  function openCreate(): void {
    setModalMode('create');
  }

  function openEdit(): void {
    setModalMode('edit');
  }

  function closeModal(): void {
    setModalMode(undefined);
  }

  return (
    <Group gap={'xs'} wrap={'nowrap'}>
      <Box flex={1} miw={0}>
        <CustomFieldSelect
          contexts={[FieldCollection.Person]}
          customField={customField}
          disabled={disabled}
          isOptOut={importer.isOptOut}
          // Table cells clip overflow, so the menu renders on the body.
          menuPortalTarget={document.body}
          object={column}
          onRequestCreate={openCreate}
          showValueIcon={true}
        />
      </Box>
      {newField && (
        <Button
          aria-label={t('Edit new field')}
          disabled={disabled}
          onClick={openEdit}
          tooltip={t('Edit new field')}
          type={'icon'}
        >
          <FontAwesomeIcon icon={faPen} />
        </Button>
      )}
      {modalMode && (
        <CreateImportFieldModal
          column={column}
          // "Create new field" makes another field even when the row already has a new one.
          customField={modalMode === 'edit' ? newField : undefined}
          importer={importer}
          onClose={closeModal}
          opened={true}
        />
      )}
    </Group>
  );
}

export default observer(ImportMappingFieldCell);
